import { message } from "antd";
import type { Category, ContentTopic } from "api/config/chalice-types";
import { genericError } from "fixtures/globalConstants";
import { useCategories } from "hooks/useCategoryCRUD";
import { useCallback } from "react";
import { saveTopics } from "./topicActionUtils"; // Correct import path
import { CommonActionProps } from "./useTopicActions"; // Import common props type
import { CategoryModalData } from "./useTopicsPageModals";

// --- Define Prop Types --- //
// Use CommonActionProps from useTopicActions.ts
export interface UseCategoryActionsProps extends CommonActionProps {
  serverTopics: ContentTopic[]; // Needed for reverting moves
  categories: Category[];
  categoryName: string;
  currentCategoryModalData: CategoryModalData | null;
  closeCategoryModal: () => void;
  createCategory: ReturnType<typeof useCategories>["createCategory"];
  updateCategory: ReturnType<typeof useCategories>["updateCategory"];
  deleteCategory: ReturnType<typeof useCategories>["deleteCategory"];
}

// --- Define Return Type --- //
export interface UseCategoryActionsReturn {
  handleCategorySubmit: (selectedColor: string) => void;
  handleDeleteCategory: (category: Category) => Promise<void>;
  handleMoveCategoryTopicsToUncategorized: (
    category: Category
  ) => Promise<void>;
  handleToggleCategoryEnabled: (
    categoryId: string | null | undefined,
    enable: boolean,
    categoryNameForMessage: string
  ) => void;
  handleDeleteCategoryAndAllTopics: (category: Category) => Promise<void>;
}

// --- Hook: useCategoryActions --- //
export const useCategoryActions = ({
  displayTopics,
  setDisplayTopics,
  serverTopics, // Include serverTopics here
  currentBusiness,
  dispatch,
  minimumTopicsMessage,
  categories,
  categoryName,
  currentCategoryModalData,
  closeCategoryModal,
  createCategory,
  updateCategory,
  deleteCategory,
}: UseCategoryActionsProps): UseCategoryActionsReturn => {
  const saveTopicsWrapper = (newT: ContentTopic[], origT: ContentTopic[]) =>
    saveTopics(newT, origT, setDisplayTopics, dispatch, currentBusiness?.id);

  const handleCategorySubmit = useCallback(
    async (selectedColor: string) => {
      if (!currentBusiness?.id) {
        message.error("Cannot save category without a business ID");
        return;
      }
      if (!categoryName.trim()) {
        message.error("Category name cannot be empty");
        return;
      }

      const existingCategory = categories.find(
        (cat) =>
          cat.name.toLowerCase() === categoryName.trim().toLowerCase() &&
          cat.id !== currentCategoryModalData?.categoryId
      );
      if (existingCategory) {
        message.error(`Category "${categoryName.trim()}" already exists`);
        return;
      }

      try {
        if (currentCategoryModalData?.type === "new-category") {
          await createCategory({
            name: categoryName.trim(),
            description: "",
            color: selectedColor,
          });
          // message.success(`Category "${categoryName.trim()}" created`);
        } else if (
          currentCategoryModalData?.type === "edit-category" &&
          currentCategoryModalData.categoryId
        ) {
          await updateCategory({
            categoryId: currentCategoryModalData.categoryId,
            updateData: {
              name: categoryName.trim(),
              color: selectedColor,
            },
          });
          // message.success(`Category updated`);
        }
        closeCategoryModal();
      } catch (error) {
        genericError(error);
      }
    },
    [
      categoryName,
      categories,
      currentCategoryModalData,
      closeCategoryModal,
      createCategory,
      updateCategory,
      currentBusiness?.id,
    ]
  );

  const handleDeleteCategory = useCallback(
    async (category: Category) => {
      if (category.id && currentBusiness?.id) {
        try {
          await deleteCategory(category.id);
          // message.success("Category deleted");
        } catch (error) {
          genericError(error);
        }
      } else {
        message.error("Cannot delete category without a valid ID");
      }
    },
    [deleteCategory, currentBusiness?.id]
  );

  const handleMoveCategoryTopicsToUncategorized = useCallback(
    async (category: Category) => {
      if (!category.id || !currentBusiness?.id) return;

      const originalTopics = [...serverTopics]; // Use serverTopics for original state
      const topicsToMove = originalTopics
        .filter((t) => t.category_id === category.id)
        .map((t) => ({ ...t, category_id: null }));

      if (topicsToMove.length > 0) {
        const otherTopics = originalTopics.filter(
          (t) => t.category_id !== category.id
        );
        const allUpdatedTopics = [...otherTopics, ...topicsToMove];
        await saveTopicsWrapper(allUpdatedTopics, originalTopics);
      }
      return Promise.resolve();
    },
    [serverTopics, saveTopicsWrapper, currentBusiness?.id]
  );

  const handleDeleteCategoryAndAllTopics = useCallback(
    async (category: Category) => {
      if (!category.id || !currentBusiness?.id) return;

      const originalTopics = [...displayTopics]; // Use displayTopics for current UI state
      const topicsToDelete = originalTopics.filter(
        (t) => t.category_id === category.id
      );

      if (topicsToDelete.length > 0) {
        const newTopics = originalTopics.filter(
          (t) => t.category_id !== category.id
        );
        try {
          await saveTopicsWrapper(newTopics, originalTopics);
        } catch (error) {
          message.error(
            `Failed to delete category "${category.name}" and topics in category`
          );
          throw error;
        }
      }

      try {
        await deleteCategory(category.id);
        // message.success(
        //   `Successfully deleted category "${category.name}" and its topics`
        // );
      } catch (error) {
        message.error(
          `Failed to delete category "${category.name}" after deleting topics`
        );
        genericError(error);
      }
    },
    [displayTopics, saveTopicsWrapper, deleteCategory, currentBusiness?.id]
  );

  const handleToggleCategoryEnabled = useCallback(
    (
      categoryId: string | null | undefined,
      enable: boolean,
      categoryNameForMessage: string
    ) => {
      const targetCategoryId = categoryId;
      const topicsInCategory = displayTopics.filter(
        (t: ContentTopic) => t.category_id === targetCategoryId
      );
      const currentEnabledCount = displayTopics.filter(
        (t: ContentTopic) => t.enabled
      ).length;

      if (!enable) {
        const currentlyEnabledInCategory = topicsInCategory.filter(
          (t: ContentTopic) => t.enabled
        ).length;
        if (currentEnabledCount - currentlyEnabledInCategory < 1) {
          minimumTopicsMessage();
          return;
        }
      }

      const originalTopics = [...displayTopics];
      const newTopics = displayTopics.map((topic: ContentTopic) => {
        if (topic.category_id === targetCategoryId) {
          return topic.enabled === enable
            ? topic
            : { ...topic, enabled: enable };
        }
        return topic;
      });

      if (JSON.stringify(newTopics) !== JSON.stringify(originalTopics)) {
        saveTopicsWrapper(newTopics, originalTopics)
          .then(() => {
            message.success(
              `Topics in "${categoryNameForMessage}" ${enable ? "enabled" : "disabled"}`
            );
          })
          .catch(() => {
            message.error(
              `Failed to ${enable ? "enable" : "disable"} topics in "${categoryNameForMessage}"`
            );
          });
      } else {
        message.info(
          `All topics in "${categoryNameForMessage}" are already ${enable ? "enabled" : "disabled"}`
        );
      }
    },
    [displayTopics, saveTopicsWrapper, minimumTopicsMessage]
  );

  return {
    handleCategorySubmit,
    handleDeleteCategory,
    handleMoveCategoryTopicsToUncategorized,
    handleDeleteCategoryAndAllTopics,
    handleToggleCategoryEnabled,
  };
};
